// 认证状态：当前用户 + access_token（localStorage 持久化）。
// token 由 api/client.ts 的 authHeader() 注入请求；此处只管状态。
// 游客可用（浏览/编辑/导出），登录才能保存项目——前端门控。
import { create } from 'zustand'
import type { AuthResponse } from '../api/types'

const TOKEN_KEY = 'vw_token'
const USER_KEY = 'vw_user'

export interface AuthUser {
  id: string
  email: string
  name: string
  plan?: string
}

interface AuthState {
  user: AuthUser | null
  token: string | null
  hydrated: boolean
  /** 登录弹窗可见性（提升到 store，便于任意组件引导登录，如 AI 填充未登录时） */
  authModalOpen: boolean
  openAuthModal: () => void
  closeAuthModal: () => void
  /** 从 localStorage 恢复（应用启动 / Nav mount 时调一次） */
  hydrate: () => void
  /** 登录/注册成功后存 token + user */
  setAuth: (resp: AuthResponse) => void
  /** 登出：清空状态 + localStorage */
  logout: () => void
}

function readStored(): { token: string | null; user: AuthUser | null } {
  if (typeof localStorage === 'undefined') return { token: null, user: null }
  const token = localStorage.getItem(TOKEN_KEY)
  const userRaw = localStorage.getItem(USER_KEY)
  let user: AuthUser | null = null
  try {
    user = userRaw ? (JSON.parse(userRaw) as AuthUser) : null
  } catch {
    user = null
  }
  return { token, user }
}

// 初始即从 localStorage 读取（SPA 客户端，避免首屏闪烁登录按钮）
export const useAuthStore = create<AuthState>((set) => {
  const stored = readStored()
  return {
    user: stored.user,
    token: stored.token,
    hydrated: true,
    authModalOpen: false,
    openAuthModal: () => set({ authModalOpen: true }),
    closeAuthModal: () => set({ authModalOpen: false }),
    hydrate: () => {
      const s = readStored()
      set({ token: s.token, user: s.user, hydrated: true })
    },
  setAuth: (resp) => {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(TOKEN_KEY, resp.access_token)
      localStorage.setItem(USER_KEY, JSON.stringify(resp.user))
    }
    set({ token: resp.access_token, user: resp.user as unknown as AuthUser })
  },
  logout: () => {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(TOKEN_KEY)
      localStorage.removeItem(USER_KEY)
    }
    set({ token: null, user: null })
  },
  }
})
